/**
 * AddMaterialFromMissingModal – Phase 4: Add Material vanuit Missing Materials Report
 * Opent bestaande "Add Material" flow met vooringevulde description, unit en price uit PDF-import.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Dialog } from '../Dialog';
import type { MissingMaterialItem } from './MissingMaterialsWidget';
import { get422FieldErrors, getApiErrorMessage } from '../../utils/apiErrors';

interface AddMaterialFromMissingModalProps {
  item: MissingMaterialItem;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddMaterialFromMissingModal({ item, onClose, onSuccess }: AddMaterialFromMissingModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [naam, setNaam] = useState(item.description);
  const [eenheid, setEenheid] = useState(item.unit || 'stuk');
  const [prijs, setPrijs] = useState(String(item.avg_price));
  const [materiaalGroepId, setMateriaalGroepId] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: groepen = [] } = useQuery({
    queryKey: ['materiaal-groepen'],
    queryFn: async () => {
      const response = await api.get('/materiaal-groepen');
      return response.data;
    },
  });

  // Default naar "toebehoren" of eerste groep
  useEffect(() => {
    if (groepen.length && !materiaalGroepId) {
      const toebehoren = groepen.find(
        (g: { naam?: string }) => (g.naam || '').toLowerCase().includes('toebehoren')
      );
      setMateriaalGroepId(String(toebehoren?.id ?? groepen[0]?.id ?? ''));
    }
  }, [groepen, materiaalGroepId]);

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await api.post('/materialen', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['materialen']);
      queryClient.invalidateQueries(['missing-materials-report']);
      onSuccess();
    },
    onError: (error: { response?: { status?: number; data?: unknown } }) => {
      if (error.response?.status === 422 && error.response?.data) {
        setFieldErrors(get422FieldErrors(error.response.data));
      } else {
        setFieldErrors({});
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const naamTrim = naam.trim();
    if (!naamTrim) {
      setFieldErrors({ naam: t('materialen.naamVerplicht', 'Naam is verplicht') });
      return;
    }
    const groepId = parseInt(materiaalGroepId, 10);
    if (!groepId || Number.isNaN(groepId)) {
      setFieldErrors({ materiaal_groep_id: t('materialen.groepVerplicht', 'Materiaalgroep is verplicht') });
      return;
    }
    const prijsVal = parseFloat(prijs);
    const payload = {
      naam: naamTrim,
      materiaal_groep_id: groepId,
      materiaal_groep_ids: [groepId],
      eenheid: eenheid || 'stuk',
      prijs_aankoop_per_eenheid: Number.isNaN(prijsVal) ? null : prijsVal,
      berekeningstype: 'master_marge',
      is_actief: true,
    };
    createMutation.mutate(payload);
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white';

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('procurement.missingMaterials.addMaterialTitle', { defaultValue: 'Materiaal toevoegen aan bibliotheek' })}
      size="md"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('procurement.missingMaterials.addMaterialDesc', {
            defaultValue: 'Vul eventueel aan en voeg toe aan de Global Material Library. Velden zijn vooringevuld uit PDF-import.',
          })}
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('common.name', 'Naam')} *
          </label>
          <input
            type="text"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            className={inputClass}
            required
          />
          {fieldErrors.naam && <p className="mt-1 text-sm text-red-600">{fieldErrors.naam}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('procurement.missingMaterials.unit', { defaultValue: 'Eenheid' })}
          </label>
          <select
            value={eenheid}
            onChange={(e) => setEenheid(e.target.value)}
            className={inputClass}
          >
            {['stuk', 'm', 'm²', 'uur', 'rol', 'set'].map((u) => (
              <option key={u} value={u}>
                {u === 'm²' ? 'm²' : u === 'm' ? 'm' : u}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('procurement.missingMaterials.avgPrice', { defaultValue: 'Inkoopprijs per eenheid' })}
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={prijs}
            onChange={(e) => setPrijs(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('materialen.materiaalGroep', 'Materiaalgroep')} *
          </label>
          <select
            value={materiaalGroepId}
            onChange={(e) => setMateriaalGroepId(e.target.value)}
            className={inputClass}
            required
          >
            <option value="">{t('materialen.selecteerGroep', 'Selecteer groep')}</option>
            {groepen.map((g: { id: number; naam: string }) => (
              <option key={g.id} value={g.id}>
                {g.naam}
              </option>
            ))}
          </select>
          {fieldErrors.materiaal_groep_id && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.materiaal_groep_id}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {t('common.cancel', 'Annuleren')}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending
              ? t('common.saving', 'Opslaan...')
              : t('procurement.missingMaterials.addToLibrary', { defaultValue: 'Toevoegen aan bibliotheek' })}
          </button>
        </div>

        {createMutation.isError && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(createMutation.error as { response?: { data?: { detail?: string } } })}
          </p>
        )}
      </form>
    </Dialog>
  );
}
